import { useEffect, useRef } from "react";
import { SmallArrow } from "./Arrow";
import { barHeightPx, dirLevelColor, fmtSpeed, knotsColor } from "../lib/utils";
import { dayName, dayNum } from "../lib/utils";
import type { EnrichedSpot, ScoredDay, ScoredHour, SpeedUnit } from "../lib/types";
import styles from "./HourlyChart.module.css";

interface HourlyChartProps {
  spot: EnrichedSpot;
  visibleDays: number;
  unit: SpeedUnit;
  selectedHourIdx: number;
  /** Tick that bumps when an external (day-pill) scroll-to-day request arrives. */
  scrollToDayTick: number;
  /** Day to scroll to when scrollToDayTick changes. */
  scrollToDayOffset: number;
  /** Bar tap. */
  onSelectHour: (idx: number, dayOffset: number) => void;
  /** Reports the day under the chart's viewport center after user scrolls. */
  onActiveDayChange: (dayOffset: number) => void;
}

interface FlatHour extends ScoredHour {
  dayOffset: number;
}

/**
 * Multi-day hourly bar chart. Bars are coloured by knots, scaled with a
 * sqrt curve, and grouped by day with a thin divider line.
 *
 * Two bar embellishments:
 *   • `now`   — vertical "Now" line on the current hour
 *   • `selected` — vertical line + dark overlay on the user's pick
 *
 * Sparse meta: every 3rd bar (by hour-of-day mod 3) shows the full
 * arrow + speed + gust + hour text; the rest only show the arrow.
 */
export function HourlyChart({
  spot,
  visibleDays,
  unit,
  selectedHourIdx,
  scrollToDayTick,
  scrollToDayOffset,
  onSelectHour,
  onActiveDayChange,
}: HourlyChartProps) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const dayStripRef = useRef<HTMLDivElement | null>(null);

  const days: ScoredDay[] = spot.days.slice(0, visibleDays);
  // Include hours from every day — including "skip" days. Skip means we
  // don't have a useful daytime summary for the overview row, but the
  // hourly data may still be valid (e.g. today's remaining evening
  // hours when the page loads after 19:00). Excluding them caused the
  // day-strip labels to misalign with the bars below.
  const allHours: FlatHour[] = days.flatMap((day) =>
    day.hours.map((h) => ({ ...h, dayOffset: day.dayOffset })),
  );

  // Scroll the chart to the leftmost bar of `scrollToDayOffset` ONLY when the
  // parent bumps the tick (e.g. day-pill click). We deliberately don't depend
  // on scrollToDayOffset directly — natural user scroll updates the parent's
  // selectedDay (which is the same prop) and we don't want to snap back to it.
  // The latest offset is read via ref so the closure captures it on demand.
  const scrollTargetRef = useRef(scrollToDayOffset);
  scrollTargetRef.current = scrollToDayOffset;
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const target = chart.querySelector<HTMLElement>(
      `[data-day-offset="${scrollTargetRef.current}"][data-hour-idx]`,
    );
    if (target) {
      chart.scrollTo({
        left: Math.max(0, target.offsetLeft - 16),
        behavior: scrollToDayTick === 0 ? "auto" : "smooth",
      });
    }
  }, [scrollToDayTick]);

  // Chart scroll → mirror to label strip + debounce-detect active day.
  useEffect(() => {
    const chart = chartRef.current;
    const strip = dayStripRef.current;
    if (!chart) return;
    let timer: number | null = null;

    const onScroll = () => {
      if (strip) strip.scrollLeft = chart.scrollLeft;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const center = chart.scrollLeft + chart.clientWidth / 2;
        let bestDay: number | null = null;
        let bestDist = Number.POSITIVE_INFINITY;
        for (const bar of chart.querySelectorAll<HTMLElement>("[data-day-offset][data-hour-idx]")) {
          const dayAttr = bar.dataset.dayOffset;
          if (!dayAttr) continue;
          const dist = Math.abs(bar.offsetLeft + 10 - center);
          if (dist < bestDist) {
            bestDist = dist;
            bestDay = Number(dayAttr);
          }
        }
        if (bestDay !== null) onActiveDayChange(bestDay);
      }, 80);
    };

    chart.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      chart.removeEventListener("scroll", onScroll);
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [onActiveDayChange]);

  if (allHours.length === 0) {
    return <p className={styles.hcEmpty}>No data</p>;
  }

  return (
    <>
      <div className={styles.hourlyChart} ref={chartRef}>
        {allHours.map((h, i) => {
          const speedKn = h.speedMs * 1.944;
          const fillH = barHeightPx(speedKn);
          const color = speedKn < 3 ? "var(--wind-nil)" : knotsColor(speedKn);
          const gustKn = h.gustMs * 1.944;
          const gustH = barHeightPx(gustKn);
          const showGust = gustKn > speedKn + 0.5;
          const hr = Number.parseInt(h.time.slice(11, 13), 10);
          const timeStr = h.time.slice(11, 16).replace(":", ".");
          const showMeta = (hr - 6) % 3 === 0;
          // Arrow colour reflects direction match only (speed is shown by
          // bar height + colour). Calm hours (<3 kn) stay muted because
          // direction is meaningless when there's effectively no wind.
          const arrowColor =
            speedKn < 3
              ? "var(--foreground-muted)"
              : dirLevelColor(h.dirDeg, spot.ideal.dirs);
          const prev = allHours[i - 1];
          const isDayStart = i > 0 && prev !== undefined && h.dayOffset !== prev.dayOffset;
          const isSelected = i === selectedHourIdx;

          return (
            <button
              type="button"
              key={`${h.dayOffset}-${h.time}`}
              className={[
                styles.hcBar,
                isSelected ? styles.hcBarSelected : "",
                h.isNow ? styles.hcBarNow : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-hour-idx={i}
              data-day-offset={h.dayOffset}
              onClick={() => onSelectHour(i, h.dayOffset)}
            >
              <div className={styles.hcColInner}>
                {isDayStart && <div className={styles.hcDayLine} />}
                <span className={styles.hcSelLabel}>{timeStr}</span>
                <span className={styles.hcNowLabel}>{h.isNow ? "Now" : ""}</span>
                {showGust && (
                  <div className={styles.hcGustFill} style={{ height: `${gustH}px` }} />
                )}
                <div className={styles.hcFill} style={{ height: `${fillH}px`, background: color }} />
              </div>
              <div className={[styles.hcInfo, !showMeta && styles.hcInfoSparse].filter(Boolean).join(" ")}>
                <SmallArrow dirDeg={h.dirDeg} color={arrowColor} />
                <span className={styles.hcSpd}>{fmtSpeed(h.speedMs, unit)}</span>
                <span className={styles.hcGst}>{fmtSpeed(h.gustMs, unit)}</span>
                <span className={styles.hcSep} />
                <span className={styles.hcHr}>{String(hr).padStart(2, "0")}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className={styles.hcDayStrip} ref={dayStripRef}>
        {days.map((day) => {
          if (day.hours.length === 0) return null;
          const label =
            day.dayOffset === 0
              ? "Today"
              : day.dayOffset === 1
                ? "Tomorrow"
                : `${dayName(day.date)} ${dayNum(day.date)}`;
          // Each chart bar is 20px wide with a 4px gap → 24px per slot.
          // Cell width tracks the day's actual bar count so the label
          // stays aligned with the bars below regardless of partial
          // first/last days (e.g. today after 19:00 with only 3 hours
          // left, or the tail end of the SMHI hourly window).
          const w = day.hours.length * 24;
          return (
            <div
              key={day.dayOffset}
              className={styles.hcdCell}
              style={{ width: `${w}px` }}
            >
              {label}
            </div>
          );
        })}
      </div>
    </>
  );
}
